import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, TrendingUp, MessageSquare, Code, FileText, ArrowLeft } from 'lucide-react';
import SessionManager from '../utils/sessionManager';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://aptirix-backend-469811621499.asia-south1.run.app';

export default function CourseTeachingReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const candidateSession = SessionManager.getSession('candidate');
  const token = candidateSession?.access_token || localStorage.getItem('token');
  
  useEffect(() => {
    if (sessionId && sessionId !== 'undefined') {
      fetchReport();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);
  
  const fetchReport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/course-teaching/report/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch report');
      
      const data = await response.json();
      setReport(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Report not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  const getGradeColor = (grade) => {
    if (grade === 'A+' || grade === 'A') return 'text-green-600 bg-green-100';
    if (grade === 'B+' || grade === 'B') return 'text-blue-600 bg-blue-100';
    if (grade === 'C+' || grade === 'C') return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Back to Course
        </button>
        
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Teaching Session Report
              </h1>
              <p className="text-gray-600">{report.course_title}</p>
              <p className="text-gray-500">Module {report.module_number}: {report.module_title}</p>
            </div>
            
            <div className={`px-6 py-3 rounded-lg ${getGradeColor(report.evaluation_grade)}`}>
              <p className="text-sm font-semibold">Grade</p>
              <p className="text-4xl font-bold">{report.evaluation_grade}</p>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="text-blue-600" size={20} />
                <p className="text-sm text-blue-900 font-semibold">Score</p>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {report.evaluation_score?.toFixed(1)}%
              </p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="text-green-600" size={20} />
                <p className="text-sm text-green-900 font-semibold">Engagement</p>
              </div>
              <p className="text-2xl font-bold text-green-600">
                {report.engagement_score?.toFixed(1)}%
              </p>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="text-purple-600" size={20} />
                <p className="text-sm text-purple-900 font-semibold">Questions</p>
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {report.questions_asked_count || 0}
              </p>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-orange-600" size={20} />
                <p className="text-sm text-orange-900 font-semibold">Duration</p>
              </div>
              <p className="text-2xl font-bold text-orange-600">
                {formatDuration(report.duration_seconds || 0)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Module Summary */}
        {report.module_summary && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Module Summary</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{report.module_summary}</p>
          </div>
        )}
        
        {/* AI Feedback */}
        {report.ai_feedback && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">AI Feedback</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{report.ai_feedback}</p>
          </div>
        )}
        
        {/* Strengths & Improvements */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {report.strengths && report.strengths.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-green-900 mb-4">Strengths</h2>
              <ul className="space-y-2">
                {report.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {report.improvements && report.improvements.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-orange-900 mb-4">Areas for Improvement</h2>
              <ul className="space-y-2">
                {report.improvements.map((improvement, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span className="text-gray-700">{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Evaluation Q&A */}
        {report.evaluation_questions && report.evaluation_questions.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Evaluation Questions</h2>
            <div className="space-y-4">
              {report.evaluation_questions.map((q, index) => {
                const answer = report.evaluation_answers?.[index];
                return (
                  <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                    <p className="font-semibold text-gray-900 mb-2">
                      Q{index + 1}: {q.question_text || q.question}
                    </p>
                    {answer && (
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        <span className="font-semibold">Your Answer:</span> {answer.answer}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Code Snippets */}
        {report.code_snippets && report.code_snippets.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Code size={24} />
              Code Examples Covered
            </h2>
            <div className="space-y-4">
              {report.code_snippets.map((snippet, index) => (
                <div key={index} className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 text-sm font-semibold">
                      {snippet.language}
                    </span>
                  </div>
                  <pre className="text-gray-100 text-sm overflow-x-auto">
                    {snippet.code}
                  </pre>
                  {snippet.explanation && (
                    <p className="text-gray-400 text-sm mt-2">{snippet.explanation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate(`/courses/${report.course_id}`)}
            className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            Continue to Next Module
          </button>
          <button
            onClick={() => window.print()}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
          >
            Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
